/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/utils/chunk-error-handler.ts
 * 版本: v1.0
 * 创建日期: 2026-03-24
 * 创建人: AI Agent
 *
 * SPA 动态 import chunk 加载失败自动恢复工具
 *
 * 问题场景：
 *   前端部署新版本后，旧 chunk 文件被清除。用户浏览器仍持有旧 index.html，
 *   切换路由时 dynamic import() 请求旧 chunk → 404 → Vue Router 导航失败 → 跳 /404
 *
 * 解决方案：
 *   1. router.onError 捕获 chunk 加载失败
 *   2. 自动 reload 页面（拉取最新 index.html）
 *   3. 使用 sessionStorage 标志防止无限刷新
 */

/**
 * 判断一个错误是否为动态 import chunk 加载失败
 *
 * @param error - 捕获到的错误对象
 * @returns boolean - 是否为 chunk 加载失败
 *
 * 常见错误特征：
 *   - TypeError: Failed to fetch dynamically imported module: ...
 *   - TypeError: error loading dynamically imported module
 *   - ChunkLoadError (webpack)
 *   - Loading chunk xxx failed (webpack)
 *   - Loading CSS chunk xxx failed
 *   - Importing a module script failed (Safari)
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const message = error instanceof Error ? error.message : String(error)
  const name = error instanceof Error ? error.name : ''

  const chunkErrorPatterns = [
    // Vite / ESM 动态导入失败
    /Failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /Importing a module script failed/i,
    // Webpack chunk 加载失败
    /Loading chunk \S+ failed/i,
    /Loading CSS chunk \S+ failed/i,
    /ChunkLoadError/i,
    // 通用网络/加载错误
    /Failed to load module script/i,
    /NetworkError when attempting to fetch/i,
    // Vite 预构建依赖
    /Unable to preload CSS/i,
  ]

  return (
    chunkErrorPatterns.some((pattern) => pattern.test(message)) ||
    chunkErrorPatterns.some((pattern) => pattern.test(name))
  )
}

/** sessionStorage key：标记已自动刷新，防止无限循环 */
const RELOAD_FLAG_KEY = '__chunk_reload_attempted__'

/**
 * 尝试自动恢复 chunk 加载失败：刷新页面获取最新 index.html
 *
 * @param targetPath - 用户尝试访问的路由路径（用于刷新后恢复导航）
 *
 * 流程：
 *   1. 检查 sessionStorage 标志，若已刷新过则不再重复（防止死循环）
 *   2. 设置标志位
 *   3. 强制 reload（不走缓存）
 *
 * 刷新后：
 *   - 浏览器获取最新 index.html → 引用最新 chunk 文件 → 正常渲染
 *   - setupChunkErrorHandler 会在路由成功后清除标志位
 */
export function attemptChunkReload(targetPath?: string): void {
  const reloadKey = `${RELOAD_FLAG_KEY}${targetPath || ''}`

  // 已尝试过刷新，不再重复（防止无限循环）
  if (sessionStorage.getItem(reloadKey)) {
    console.warn(
      '[ChunkErrorHandler] 已尝试过自动刷新但仍失败，可能是网络问题或服务器故障。',
      '请手动刷新页面（Ctrl+Shift+R）。'
    )
    // 清除标志，允许用户下次手动操作后再自动恢复
    sessionStorage.removeItem(reloadKey)
    return
  }

  console.info('[ChunkErrorHandler] 检测到页面资源版本不一致，自动刷新加载最新版本...')

  // 设置标志位
  sessionStorage.setItem(reloadKey, Date.now().toString())

  // 强制刷新（Vite 场景下 location.reload() 即可，浏览器会重新请求 index.html）
  window.location.reload()
}

/**
 * 为 Vue Router 安装 chunk 加载失败自动恢复机制
 *
 * @param router - Vue Router 实例（需要 onError 和 afterEach 方法）
 *
 * 使用方式（在 router/index.ts 中）：
 * ```ts
 * import { setupChunkErrorHandler } from '@shared/utils/chunk-error-handler'
 *
 * const router = createRouter({ ... })
 * setupChunkErrorHandler(router)
 * export default router
 * ```
 */
export function setupChunkErrorHandler(router: {
  onError: (handler: (error: Error, to: { fullPath: string }, from: { fullPath: string }) => void) => void
  afterEach: (handler: (to: { fullPath: string }) => void) => void
}): void {
  // 路由错误处理：捕获 chunk 加载失败
  router.onError((error, to, _from) => {
    if (isChunkLoadError(error)) {
      console.warn(
        `[ChunkErrorHandler] Chunk 加载失败，目标路由: ${to.fullPath}`,
        error.message
      )
      attemptChunkReload(to.fullPath)
    } else {
      // 非 chunk 错误，正常抛出
      console.error('[Router] 路由错误:', error)
    }
  })

  // 路由成功后，清除该路径的刷新标志（说明刷新成功了）
  router.afterEach((to) => {
    const reloadKey = `${RELOAD_FLAG_KEY}${to.fullPath}`
    if (sessionStorage.getItem(reloadKey)) {
      sessionStorage.removeItem(reloadKey)
      console.info('[ChunkErrorHandler] 页面已成功加载最新版本 ✅')
    }
  })

  // 页面加载时清理所有过期的刷新标志（超过 30 秒的视为过期）
  try {
    const now = Date.now()
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(RELOAD_FLAG_KEY)) {
        const timestamp = parseInt(sessionStorage.getItem(key) || '0', 10)
        if (now - timestamp > 30_000) {
          sessionStorage.removeItem(key)
        }
      }
    }
  } catch {
    // sessionStorage 访问失败（隐私模式等），忽略
  }
}
